import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Toaster } from "@/gestionale/components/ui/toaster";
import { Toaster as Sonner } from "@/gestionale/components/ui/sonner";
import { TooltipProvider } from "@/gestionale/components/ui/tooltip";
import AppLayout from "@/gestionale/components/AppLayout";
import { AuthProvider, useAuth } from "@/gestionale/hooks/useAuth";
import { supabase } from "@/gestionale/integrations/supabase/client";

import Dashboard from "@/gestionale/pages/Dashboard";
import AtletaDashboard from "@/gestionale/pages/AtletaDashboard";
import AllenatoreDashboard from "@/gestionale/pages/AllenatoreDashboard";
import IstruttoreDashboard from "@/gestionale/pages/IstruttoreDashboard";
import Persone from "@/gestionale/pages/Persone";
import CertificatiMedici from "@/gestionale/pages/CertificatiMedici";
import Tesseramenti from "@/gestionale/pages/Tesseramenti";
import Abbonamenti from "@/gestionale/pages/Abbonamenti";
import Soci from "@/gestionale/pages/Soci";
import Contabilita from "@/gestionale/pages/Contabilita";
import Comunicazioni from "@/gestionale/pages/Comunicazioni";
import Report from "@/gestionale/pages/Report";
import Presenze from "@/gestionale/pages/Presenze";
import TessereIngressi from "@/gestionale/pages/TessereIngressi";
import Impostazioni from "@/gestionale/pages/Impostazioni";
import MigrazioneMovimenti from "@/gestionale/pages/MigrazioneMovimenti";
import Gioco from "@/gestionale/pages/Gestionale Gioco";
import Sponsor from "@/gestionale/pages/Sponsor";
import Partite from "@/gestionale/pages/Partite";
import IARubiana from "@/gestionale/pages/IARubiana";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

function ProtectedRoutes() {
  const { user, role, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-yellow-400" />
      </div>
    );
  }

  // Not logged in → bounce to the site login page (the site owns auth)
  if (!user) return <Navigate to="/login" replace />;

  if (!role) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 text-center bg-zinc-950 text-zinc-100">
        <div className="space-y-3 max-w-md">
          <h2 className="text-xl font-display font-bold">Accesso in attesa</h2>
          <p className="text-zinc-400 text-sm">
            Il tuo account non ha ancora un ruolo assegnato nel gestionale. Contatta l'amministratore della società.
          </p>
          <button
            onClick={() => supabase.auth.signOut()}
            className="text-yellow-400 underline text-sm"
          >
            Esci
          </button>
        </div>
      </div>
    );
  }

  const isAdmin = role === "admin";
  const isSegreteria = role === "segreteria";
  const isAtleta = role === "atleta";
  const isAllenatore = role === "allenatore";
  const isIstruttore = role === "istruttore";
  const hasFullAccess = isAdmin || isSegreteria;

  // Role-isolated dashboards: atleta / allenatore / istruttore each see
  // only their dedicated dashboard. They manage their internal tabs from
  // within their own page (no access to /app/* general menu), ma il
  // minigioco è raggiungibile da chiunque abbia un account nel gestionale,
  // a prescindere dal ruolo — per questo la rotta /gioco è aggiunta anche
  // qui, non solo nel blocco admin/segreteria.
  if (isAtleta) {
    return (
      <AppLayout>
        <Routes>
          <Route path="/" element={<AtletaDashboard />} />
          <Route path="/gioco" element={<Gioco />} />
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
      </AppLayout>
    );
  }

  if (isAllenatore) {
    return (
      <AppLayout>
        <Routes>
          <Route path="/" element={<AllenatoreDashboard />} />
          <Route path="/gioco" element={<Gioco />} />
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
      </AppLayout>
    );
  }

  if (isIstruttore) {
    return (
      <AppLayout>
        <Routes>
          <Route path="/" element={<IstruttoreDashboard />} />
          <Route path="/gioco" element={<Gioco />} />
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
      </AppLayout>
    );
  }

  // Admin / segreteria: full menu access
  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/comunicazioni" element={<Comunicazioni />} />
        <Route path="/abbonamenti" element={<Abbonamenti />} />
        <Route path="/persone" element={<Persone />} />
        <Route path="/certificati-medici" element={<CertificatiMedici />} />
        <Route path="/tesseramenti" element={<Tesseramenti />} />
        <Route path="/soci" element={<Soci />} />
        <Route path="/contabilita" element={<Contabilita />} />
        <Route path="/report" element={<Report />} />
        <Route path="/presenze" element={<Presenze />} />
        {isAdmin && <Route path="/partite" element={<Partite />} />}
        <Route path="/tessere-ingressi" element={<TessereIngressi />} />
        <Route path="/gioco" element={<Gioco />} />
        <Route path="/sponsor" element={<Sponsor />} />
        {isAdmin && <Route path="/impostazioni" element={<Impostazioni />} />}
        {isAdmin && <Route path="/ia-rubiana" element={<IARubiana />} />}
        {isAdmin && <Route path="/migrazione-movimenti" element={<MigrazioneMovimenti />} />}
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </AppLayout>
  );
}

export default function GestionaleApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <ProtectedRoutes />
        </AuthProvider>
        <Toaster />
        <Sonner />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
